import { storage } from '@/lib/storage';
import { PriceHistoryEntry } from '@/types';
import { nanoid } from 'nanoid';
import { productService } from './productService';

const PRICE_HISTORY_KEY = 'price_history';

export const priceHistoryService = {
  getAll: (): PriceHistoryEntry[] => {
    return storage.get<PriceHistoryEntry[]>(PRICE_HISTORY_KEY) || [];
  },

  getByProduct: (productId: string): PriceHistoryEntry[] => {
    return priceHistoryService.getAll()
      .filter(entry => entry.product_id === productId)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  },

  recordCostChange: (
    productId: string,
    oldCost: number,
    newCost: number,
    reason: 'purchase_order' | 'manual_adjustment' | 'markup_calculator',
    referenceId?: string
  ): void => {
    if (oldCost === newCost) return; // No change

    const product = productService.getById(productId);
    if (!product) return;

    const entry: PriceHistoryEntry = {
      id: nanoid(),
      product_id: productId,
      product_name: product.name,
      product_sku: product.sku,
      entry_type: 'cost_change',
      old_value: oldCost,
      new_value: newCost,
      change_reason: reason,
      reference_id: referenceId,
      created_at: new Date().toISOString(),
    };

    const history = priceHistoryService.getAll();
    history.push(entry);
    storage.set(PRICE_HISTORY_KEY, history);
  },

  recordPriceChange: (
    productId: string,
    oldPrice: number,
    newPrice: number,
    reason: 'purchase_order' | 'manual_adjustment' | 'markup_calculator',
    referenceId?: string
  ): void => {
    if (oldPrice === newPrice) return; // No change

    const product = productService.getById(productId);
    if (!product) return;

    const entry: PriceHistoryEntry = {
      id: nanoid(),
      product_id: productId,
      product_name: product.name,
      product_sku: product.sku,
      entry_type: 'price_change',
      old_value: oldPrice,
      new_value: newPrice,
      change_reason: reason,
      reference_id: referenceId,
      created_at: new Date().toISOString(),
    };

    const history = priceHistoryService.getAll();
    history.push(entry);
    storage.set(PRICE_HISTORY_KEY, history);
  },

  getRecentChanges: (limit: number = 10): PriceHistoryEntry[] => {
    return priceHistoryService.getAll()
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, limit);
  },
};
